import { defineComponent } from 'vue';
import BaseComponent from './baseFormMixin.jsx';
import utils from '@/Shared/utils.jsx';
import { QField, QInput, QIcon, QMenu, QCard, QCardActions, QBtn, QDate } from 'quasar';
import dayjs from 'dayjs';
import locale, { careWindowsToMomentTimezoneMap } from '@/Services/locale';

function toQuasarDate(ymd) {
    if (!ymd) return null;
    return typeof ymd === 'string' ? ymd.replace(/-/g, '/') : null;
}

export default defineComponent({
    name: 'sform-singledatepicker',
    mixins: [BaseComponent],
    components: { QField, QInput, QIcon, QMenu, QCard, QCardActions, QBtn, QDate },
    data: () => ({
        titleRenderer: null,
        defaultRenderer: null,
        timeZoneRenderer: null,
        datePickerOpen: false,
        localDate: null,
        localTime: '00:00',
    }),
    props: {},
    created() {},
    mounted() {
        this.loadComplete();
        this.$nextTick(() => this.syncInitialValue());
    },
    watch: {
        itemvalue: {
            immediate: true,
            handler(v) {
                const iso = this.toIsoValue(v);
                if (iso && v !== iso) {
                    // Wait for baseFormMixin to finish creating this.writefunc
                    this.$nextTick(() => {
                        if (this.writefunc)
                            this.sync(iso);
                    });
                }
            }
        },
        datePickerOpen(isOpen) {
            if (isOpen) {
                if (this.isReadOnly) {
                    this.datePickerOpen = false;
                    return;
                }
                this.initLocalFromItemvalue();
            }
        },
    },
    computed: {
        firstDayOfWeek() {
            try {
                // Returns 0 for Sunday, 1 for Monday, etc. based on the active locale
                return dayjs().startOf('week').day();
            } catch (e) {
                return 0;
            }
        },
        default: function () {
            try {
                if (this.defaultRenderer === null && (this.itemvalue || this.element.default || this.element.schema.default))
                    this.defaultRenderer = utils.compile(this,  this.itemvalue || this.element.default || this.element.schema.default);
                if (this.defaultRenderer)
                    return utils.evaluate(this.defaultRenderer, this);
                else
                    return '';
            } catch (e) {
                utils.warn('Default could not evaluate expression: ' + (this.element.default || this.element.schema.default) + '; ' + e);
                return '';
            }
        },
        timeZone: function() {
            try {
                if (this.timeZoneRenderer === null && this.element.schema.timeZone)
                    this.timeZoneRenderer = utils.compile(this, this.element.schema.timeZone);
                if (this.timeZoneRenderer){
                    var passedTimeZone = utils.evaluate(this.timeZoneRenderer, this);
                    var dayjsTimezone = careWindowsToMomentTimezoneMap[passedTimeZone];
                    return dayjsTimezone;
                }
                else
                    return "";
            }
            catch (e)
            {
                utils.warn('Default could not evaluate expression: ' + this.element.schema.timeZone + '; ' + e);
                return '';
            }
        },
        // QField.modelValue is a localized display string (L LT / l). Mixin rules
        // would validate that against schema format date-time and always fail.
        // Vue2 DateRangePicker never bound rules to the display field.
        rules() {
            const r = [];
            if (this.element?.required) {
                r.push(() => {
                    const v = this.itemvalue;
                    if (v === null || v === undefined || v === '')
                        return 'Required';
                    return true;
                });
            }
            if (this.element?.schema) {
                r.push(() => {
                    const errors = utils.schema.performValidation(this.element.schema, this.itemvalue);
                    return errors.length > 0 ? errors[0].error : true;
                });
            }
            return r;
        },
        allowTime() {
            return !!this.element?.formatData?.AllowTime;
        },
        allowFutureDate() {
            return this.element?.formatData?.AllowFutureDate !== false;
        },
        allowPastDate() {
            return this.element?.formatData?.AllowPastDate !== false;
        },
        placeholderText() {
            return this.element?.title || this.Title || 'Select Date';
        },
        messageText() {
            return this.element?.schema?.description || this.element?.description || '';
        },
        // Match other schema fields (e.g. formText / DateRangePicker): Quasar
        // readonly for dashed outline + full opacity. Interaction is blocked
        // separately — QMenu disable alone does not stop the parent activator.
        isReadOnly() {
            return !!(this.readonly || this.noinput || this.appearance?.readonly);
        },
        displayValue() {
            const v = this.itemvalue;
            if (v == null || v === '') return '';
            const d = dayjs(v);
            if (!d.isValid()) return '';
            
            return this.allowTime ? d.format('L LT') : d.format('l');
        },
        quasarDateModel: {
            get() {
                return this.localDate;
            },
            set(val) {
                this.localDate = val;
            },
        },
        defaultYearMonth() {
            const v = this.itemvalue;
            if (v != null && v !== '') {
                const d = dayjs(v);
                if (d.isValid()) return d.format('YYYY/MM');
            }
            return dayjs().format('YYYY/MM');
        },
        dateOptions() {
            const allowPast = this.allowPastDate;
            const allowFuture = this.allowFutureDate;
            const futureLimit = this.element?.formatData?.FutureDateLimit;
            const pastLimit = this.element?.formatData?.PastDateLimit;
            return (dateStr) => {
                if (dateStr == null) return true;
                const str = typeof dateStr === 'string' ? dateStr.replace(/\//g, '-') : dateStr;
                const d = dayjs(str);
                if (!d.isValid()) return true;
                if (!allowPast && d.isBefore(dayjs(), 'day')) return false;
                if (!allowFuture && d.isAfter(dayjs(), 'day')) return false;
                if (pastLimit != null && d.isBefore(dayjs().subtract(pastLimit, 'day'), 'day')) return false;
                if (futureLimit != null && d.isAfter(dayjs().add(futureLimit, 'day'), 'day')) return false;
                return true;
            };
        },
    },
    methods: {
        initLocalFromItemvalue() {
            const v = this.itemvalue;
            if (v != null && v !== '') {
                const d = dayjs(v);
                if (d.isValid()) {
                    this.localDate = toQuasarDate(d.format('YYYY-MM-DD'));
                    this.localTime = d.format('HH:mm');
                    return;
                }
            }
            const def = this.default;
            if (def != null && def !== '' && typeof def === 'object' && def !== 'today') {
                const d = dayjs(def);
                if (d.isValid()) {
                    this.localDate = toQuasarDate(d.format('YYYY-MM-DD'));
                    this.localTime = d.format('HH:mm');
                    return;
                }
            }
            const now = dayjs();
            this.localDate = toQuasarDate(now.format('YYYY-MM-DD'));
            this.localTime = now.format('HH:mm');
        },
        applyDateRangeHandler(date) {
            this.sync(date);
        },
        notifyFormChanged() {
            let p = this.$parent;
            while (p && (!('cc_form_changed_indicator' in p) || !p.cc_form_changed_active))
                p = p.$parent;

            p?.formChanged?.();
        },
        toIsoValue(value) {
            if (value == null || value === '')
                return null;
            const d = dayjs(value);
            if (!d.isValid())
                return null;
            return this.allowTime ? d.toISOString() : d.startOf('day').toISOString();
        },
        syncInitialValue() {
            if (this.noinput || this.isReadOnly)
                return;

            const initial = this.itemvalue ?? this.default;
            const iso = this.toIsoValue(initial);
            if (!iso)
                return;

            this.applyDateRangeHandler(iso);
            this.$nextTick(() => this.notifyFormChanged());
        },
        applySelection() {
            if (this.isReadOnly || !this.localDate) return;
            const dateStr = this.localDate.replace(/\//g, '-');
            const iso = this.allowTime
                ? dayjs(`${dateStr}T${this.localTime}`).toISOString()
                : dayjs(dateStr).startOf('day').toISOString();
            this.applyDateRangeHandler(iso);
            this.datePickerOpen = false;
        },
        cancelSelection() {
            this.datePickerOpen = false;
        },
        onMenuModelUpdate(v) {
            if (this.isReadOnly) {
                this.datePickerOpen = false;
                return;
            }
            this.datePickerOpen = v;
        },
        openDatePicker() {
            if (this.isReadOnly)
                return;
            this.datePickerOpen = true;
        },
        toggleDatePicker(e) {
            if (this.isReadOnly)
                return;
            e?.stopPropagation?.();
            this.datePickerOpen = !this.datePickerOpen;
        },
    },
    render() {
        try {
            const qField = (
                <QField
                    modelValue={this.displayValue}
                    rules={this.rules} {...this.compilerErrorFieldProps} 
                    dense
                    outlined
                    readonly={this.isReadOnly}
                    hint={this.messageText}
                    hide-hint
                    bottom-slots={!!this.messageText}
                    label={this.placeholderText}
                    class={{
                        'sform-singledatepicker-input': true,
                        'sform-input-white': true,
                    }}
                    onBlur={this.onBlur}
                    onFocus={this.onFocus}
                >
                    {{
                        prepend: () => (
                            <QIcon name="mdi-calendar" class={this.isReadOnly ? undefined : 'cursor-pointer'} onClick={this.toggleDatePicker} />
                        ),
                        control: () => (
                            <div class="self-center full-width no-outline" tabindex="0">
                                {this.displayValue}
                            </div>
                        ),
                        default: () => (
                            <QMenu
                                modelValue={this.datePickerOpen}
                                onUpdate:modelValue={this.onMenuModelUpdate}
                                disable={this.isReadOnly}
                                close-on-content-click={false}
                                anchor="bottom left"
                                self="top left"
                            >
                                <QCard style="min-width: 280px;">
                                    <QDate
                                        modelValue={this.quasarDateModel}
                                        onUpdate:modelValue={(v) => { this.quasarDateModel = v; }}
                                        flat
                                        mask="YYYY/MM/DD"
                                        first-day-of-week={this.firstDayOfWeek}
                                        default-year-month={this.defaultYearMonth}
                                        options={this.dateOptions}
                                        minimal
                                        default={this.default}
                                        timeZone={this.timeZone}
                                        class="q-pa-sm"
                                    />
                                    {this.allowTime ? (
                                        <div class="row q-px-sm q-pb-sm">
                                            <QInput
                                                modelValue={this.localTime}
                                                onUpdate:modelValue={(v) => { this.localTime = v || '00:00'; }}
                                                dense
                                                outlined
                                                hide-bottom-space
                                                type="time"
                                                style="flex: 1; min-width: 0;"
                                            />
                                        </div>
                                    ) : null}
                                    <QCardActions align="right" class="q-pt-none">
                                        <QBtn flat no-caps class="c-SentenceCaseButton" onClick={this.cancelSelection}>
                                            <span>{this.Translate ? this.Translate('Cancel') : 'Cancel'}</span>
                                        </QBtn>
                                        <QBtn flat no-caps color="primary" class="c-SentenceCaseButton" onClick={this.applySelection}>
                                            <span>{this.Translate ? this.Translate('Apply') : 'Apply'}</span>
                                        </QBtn>
                                    </QCardActions>
                                </QCard>
                            </QMenu>
                        ),
                    }}
                </QField>
            );

            return qField;
        } catch (e) {
            utils.error('formSingleDatePicker Render failed', e);
            return <div>SingleDatePicker Failed to Render {e.message}</div>;
        }
    },
});